import { useEntityClient } from '@/data/useEntityClient'
import { useWorkspaces } from '@/modules/Workspaces/useWorkspaces'
import { sameElements } from '@/shared/utils/array'
import { assertIsNotNullOrUndefined } from '@/shared/utils/typeAssertions'
import { useSidebar } from '@/sharedComponents/useSidebar'
import { useQueryClient } from '@tanstack/vue-query'
import { useDebounceFn } from '@vueuse/core'
import { useRefreshUsage } from '../Billing/useRefreshUsage'
import { entityPassesFilters } from './Filters/entityPassesFilters'
import { fieldMaybePassesFilters } from './Filters/fieldMaybePassesFilters'
import { serializeField } from './serializer'
import { useEntity } from './useEntity'
import { useFilters } from './useFilters'
import type { ProjectChannelHandlers } from './useProjectChannel'
import { useProjectStore } from './useProjectStore'

/**
 * Returns a set of handlers for the agent channel. The handlers handle
 * websocket events for the agent channel and update the stores accordingly.
 */
export const useProjectChannelHandlers = (): ProjectChannelHandlers => {
  const projectStore = useProjectStore()
  const entityStore = useEntity()
  const filterStore = useFilters()
  const workspaceStore = useWorkspaces()

  const setStale = () => {
    projectStore.setEntitiesStale(true)
    entityStore.setEntitiesStale(true)
  }

  const { isOpen: sidebarIsOpen } = useSidebar()
  const { refreshUsage, hasSkippedRefresh } = useRefreshUsage()
  /**
   * The usage towards the workspace's current limits should be kept up to date
   * so the user can see their usage increasing as they take actions in the UI.
   */
  const refreshUsageIfSidebarIsOpen = () => {
    // Only refresh usage if the sidebar is open, as otherwise we will
    // put unnecessary load on the backend. If the sidebar is closed then
    // the user will not be able to see their usage increasing anyway.
    if (sidebarIsOpen.value) {
      refreshUsage()
      hasSkippedRefresh.value = false
    } else {
      hasSkippedRefresh.value = true
    }
  }

  const entityClient = useEntityClient()

  const queryClient = useQueryClient()
  /**
   * Invalidate all queries that have the current project ID in the query key. We
   * debounce this because a single user action can trigger multiple websocket events.
   */
  const invalidateProjectCache = useDebounceFn(
    () => {
      const projectId = projectStore.projectId
      if (!projectId) {
        return
      }

      queryClient.invalidateQueries({ predicate: (query) => query.queryKey.includes(projectId) })
    },
    1_000,
    {
      maxWait: 5_000,
    },
  )

  return {
    onUpdateProperty: (property) => {
      invalidateProjectCache()
      projectStore.upsertProperty(property)

      const propertyIsInView = projectStore.activeView?.view.propertyIds?.includes(property.id)
      if (propertyIsInView) {
        setStale()
      }
    },
    onDeleteProperty: (propertyId) => {
      invalidateProjectCache()
      projectStore.removeProperty(propertyId)
      refreshUsageIfSidebarIsOpen()
    },
    onUpdateEntity: (entity) => {
      const serializedEntity = entityClient.pushEntityJSON(entity)
      const passesCurrentFilter = entityPassesFilters(serializedEntity, filterStore.currentFilter)
      if (passesCurrentFilter) {
        projectStore.upsertEntity(serializedEntity)
      }

      refreshUsageIfSidebarIsOpen()
    },
    onCreateEntity: (entity) => {
      const serializedEntity = entityClient.pushEntityJSON(entity)
      const passesCurrentFilter = entityPassesFilters(serializedEntity, filterStore.currentFilter)
      if (passesCurrentFilter) {
        projectStore.upsertEntity(serializedEntity)
      }

      refreshUsageIfSidebarIsOpen()
    },
    onDeleteEntities: (entityIds) => {
      entityClient.removeEntities(entityIds)
      projectStore.removeEntitiesById(entityIds)
      entityStore.removeEntities(entityIds)
      refreshUsageIfSidebarIsOpen()
    },
    onDeleteActiveView: ({ entityId, viewId }) => {
      invalidateProjectCache()
      projectStore.removeEntityFromView(entityId, viewId)
      refreshUsageIfSidebarIsOpen()
    },
    onUpdateField: async (field) => {
      /**
       * This is where we handle the `field:updated` websocket message.
       * The logic here is a bit annoying when there are filters,
       * because the field itself doesn't contain all the information we need.
       *
       * On top of that, we might not even hold the field entity in the story yet,
       * because, for example, the entity got recently created, or is not in our currently loaded section.
       */

      const serializedField = serializeField(field)

      // If no filter is applied, then we can be sure that the field change should
      // be made visible in the table
      if (!filterStore.currentFilter || filterStore.currentFilter.filters.length === 0) {
        projectStore.updateField(serializedField)
        entityStore.updateField(serializedField)
        refreshUsageIfSidebarIsOpen()
        return
      }

      const hasMaybePassedFilter = fieldMaybePassesFilters(
        serializedField,
        filterStore.currentFilter,
      )

      // The new field would definitely cause the entity to fail the filter, so
      // we can remove the entity from the table
      if (!hasMaybePassedFilter) {
        projectStore.removeEntitiesById([serializedField.entityId])
        entityStore.removeEntities([serializedField.entityId])
        refreshUsageIfSidebarIsOpen()
        return
      }

      // This is where it gets tricky. The field change might cause the entity to
      // pass the filter, but we can't be sure. We need to load the full entity to
      // know for certain. This is because some of the conditions for passing the filter
      // are not present in the field object, or might be in one of the other fields, or
      // are only known on the backend.
      // An example would be someone else creating a new entity, which should be visible to
      // us, but we don't know about it yet.
      assertIsNotNullOrUndefined(workspaceStore.currentWorkspace, 'No current workspace')
      assertIsNotNullOrUndefined(projectStore.projectId, 'No current project')
      const entityResponse = await entityClient.fetchEntity({
        workspaceId: workspaceStore.currentWorkspace.id,
        projectId: projectStore.projectId,
        entityId: serializedField.entityId,
        staleTimeMs: 3000,
      })

      if (!entityResponse.ok) {
        throw new Error(entityResponse.error.message)
      }

      const entity = entityResponse.data
      const passesCurrentFilter = entityPassesFilters(entity, filterStore.currentFilter)

      if (passesCurrentFilter) {
        projectStore.upsertEntity(entity)
      }
    },
    onUpdateView: (view) => {
      invalidateProjectCache()
      const activeView = { ...projectStore.activeView?.view }
      projectStore.upsertView(view)
      if (!activeView || view.id !== projectStore.activeView?.id) {
        return
      }

      const propertyIdsHaveChanged = !sameElements(
        view.propertyIds || [],
        activeView.propertyIds || [],
      )
      const assignablePropertyHasChanged =
        activeView.assignablePropertyId !== view.assignablePropertyId
      if (propertyIdsHaveChanged || assignablePropertyHasChanged) {
        /**
         * We want to be careful when setting stale, as it will trigger a reload
         * of entities which causes the table to flicker. So only set stale state
         * if something in the view has changed which would cause the entities
         * we have loaded to be stale.
         */
        setStale()
      }
    },
    onDeleteView: (viewId) => {
      invalidateProjectCache()
      projectStore.removeView(viewId)
      refreshUsageIfSidebarIsOpen()
    },
    onSetStale: setStale,
  }
}
